/* eslint-disable jsx-a11y/media-has-caption */

import React, { useEffect, useState } from "react";

import { makeDelete } from "@culturehq/client";
import { useProfile } from "app/context";
import { canAdminSocialShare } from "app/permissions";
import { Icon, PlainButton, Thumbnail } from "@culturehq/components";
import { ProfileLink } from "app/links";
import InstagramLink from "app/links/InstagramLink";
import { getImageFromStory, Trans } from "app/utils";

import instagram from "images/social/instagram.svg";

import imagesSizes from "constants/imagesSizes";

import Subtitles from "components/common/Subtitles";
import QuickOptions from "components/display/QuickOptions";
import FeedItemTitle from "components/feed/feed-item/preview/FeedItemTitle";

import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import PinUserActivityToggle from "../PinUserActivityToggle";

const PostQuickOptions = ({
  internalShare, userActivity, onUserActivityUpdate, onUserActivityRemove
}) => {
  const { profile } = useProfile();
  const { entity: socialShare } = userActivity;

  const onSocialShareDeleted = async () => {
    if (internalShare) {
      await makeDelete(`/stories/social_share/${socialShare.id}`);
    } else {
      await makeDelete(`/stories/external_social_share/${socialShare.id}`);
    }
    onUserActivityRemove(userActivity.id);
  };

  return (
    <QuickOptions>
      <ul className="dropdown-menu">
        {profile.organizationAdmin && (
          <li>
            <PinUserActivityToggle
              userActivity={userActivity}
              onUserActivityUpdate={onUserActivityUpdate}
            />
          </li>
        )}
        {canAdminSocialShare(profile, socialShare) && (
          <li>
            <ChqModalConfirmDelete
              onAccept={onSocialShareDeleted}
              trigger={onTrigger => (
                <PlainButton onClick={onTrigger} className="link action">
                  <Icon icon="trash-a" />
                  <Trans>Delete</Trans>
                </PlainButton>
              )}
            >
              <Trans>Are you sure you want to delete this social share?</Trans>
            </ChqModalConfirmDelete>
          </li>
        )}
      </ul>
    </QuickOptions>
  );
};

const InstagramCreatorInfo = ({ story, instagramShare }) => {
  const [creator, setCreator] = useState();
  const socialShare = story?.socialShares ? story.socialShares[0] : instagramShare;

  useEffect(
    () => {
      if (story) {
        setCreator(story.creator);
      } else {
        setCreator(instagramShare.creator);
      }
    }, [instagramShare, story]
  );

  if (socialShare?.socialCompany) {
    return (
      <>
        <span className="Linkedin_Creator--Information_Creator">{socialShare.socialCompany.name}</span>
        <Trans>&apos;s story on Instagram!</Trans>
      </>
    );
  }

  if (!story) {
    return (
      <Trans>a story on Instagram!</Trans>
    );
  }

  if (socialShare?.creator?.id === creator?.id) {
    return (
      <Trans>a story on Instagram!</Trans>
    );
  }

  return (
    <>
      <ProfileLink user={creator} />
      <Trans>&apos;s story on Instagram!</Trans>
    </>
  );
};

const InstagramPostPreview = ({
  story, userActivity, instagramShare, onUserActivityUpdate, onUserActivityRemove
}) => {
  const [imageUrl, setImageUrl] = useState();
  const [media, setMedia] = useState();

  useEffect(
    () => {
      if (story) {
        setImageUrl(story.media.mediaType === "image" ? getImageFromStory(imagesSizes.NORMAL, story) : story.media.thumbnail);
        setMedia(story.media);
      } else {
        setImageUrl(instagramShare.media.mediaType === "image" ? instagramShare.media.url : instagramShare.media.thumbnail);
        setMedia(instagramShare.media);
      }
    }, [story, instagramShare]
  );

  const getAccountImage = () => {
    if (instagramShare.socialCompany) {
      return instagramShare.socialCompany.logo;
    }
    return instagramShare?.creator?.avatar?.thumbUrl || story.creator.avatar.thumbUrl;
  };

  const getAccountText = () => {
    if (instagramShare.socialCompany) {
      return instagramShare.socialCompany.name;
    }
    return instagramShare.creator.name;
  };

  const getUsername = () => {
    if (instagramShare.socialCompany) {
      return instagramShare.socialCompany.username;
    }
    return instagramShare.creator.userInstagram?.name || instagramShare.creator.name;
  };

  return (
    <div className="FeedItem__preview__photo_created Story FeedItem__preview__social">
      <FeedItemTitle
        item={userActivity}
        date={userActivity.bumpedAt}
        user={instagramShare?.creator || story.creator}
      >
        <ProfileLink user={instagramShare?.creator || story.creator} />
        {instagramShare?.creator ? (
          <span>
            {" "}
            <Trans>shared</Trans>
            {" "}
            <InstagramCreatorInfo story={story} instagramShare={instagramShare} />
          </span>
        ) : (
          <span>
            <Trans>&apos;s story has been approved to share on social!</Trans>
          </span>
        )}
        <PostQuickOptions
          internalShare={!!story}
          userActivity={userActivity}
          onUserActivityUpdate={onUserActivityUpdate}
          onUserActivityRemove={onUserActivityRemove}
        />
      </FeedItemTitle>
      <InstagramLink link={instagramShare.url}>
        <div className="FeedItem__preview__Social__Container FeedItem__preview__Instagram__Container">
          <div className="FeedItem__preview__Social__Header">
            <div className="FeedItem__preview__Social__User">
              <div className="User__Info-Container">
                <Thumbnail className="Thumbnail" image={getAccountImage()} title={instagramShare?.creator?.name} />
                <div>
                  <p className="Preview-User__Info Preview-User__Info-Name">{getAccountText()}</p>
                  <p className="Preview-User__Info-Username">@{getUsername()}</p>
                </div>
              </div>
              <img className="social-icon" src={instagram} alt="Instagram" />
            </div>
          </div>
          <div className="gallery-lightbox__photo">
            <div className="gallery-lightbox__image-container" style={imageUrl && { backgroundImage: `url(${imageUrl})` }} />
            {media?.mediaType === "image" && (
              <img
                className="gallery-lightbox__photo--image"
                src={imageUrl}
                alt={story?.imageAlt || story?.question?.question || "Instagram image"}
              />
            )}
            {media?.mediaType === "video" && (
              <>
                <video
                  controls={false}
                  className="gallery-lightbox__main-image gallery-lightbox__main-video"
                  poster={`${media.thumbnail}?not-from-cache-please`}
                  src={media.url}
                  disablePictureInPicture
                  crossOrigin="anonymous"
                  controlsList="nodownload"
                >
                  <Subtitles media={media} />
                </video>
                <Icon className="PlayIcon" icon="play-no-border" />
              </>
            )}
          </div>
          <div className="FeedItem__preview__Social__Footer">
            <div className="Instagram-Buttons">
              <PlainButton className="like-btn">
                <svg color="#8e8e8e" height="24" role="img" viewBox="0 0 24 24" width="24">
                  <path fill="#8e8e8e" d="M16.792 3.904A4.989 4.989 0 0 1 21.5 9.122c0 3.072-2.652 4.959-5.197 7.222-2.512 2.243-3.865 3.469-4.303 3.752-.477-.309-2.143-1.823-4.303-3.752C5.141 14.072 2.5 12.167 2.5 9.122a4.989 4.989 0 0 1 4.708-5.218 4.21 4.21 0 0 1 3.675 1.941c.84 1.175.98 1.763 1.12 1.763s.278-.588 1.11-1.766a4.17 4.17 0 0 1 3.679-1.938m0-2a6.04 6.04 0 0 0-4.797 2.127 6.052 6.052 0 0 0-4.787-2.127A6.985 6.985 0 0 0 .5 9.122c0 3.61 2.55 5.827 5.015 7.97.283.246.569.494.853.747l1.027.918a44.998 44.998 0 0 0 3.518 3.018 2 2 0 0 0 2.174 0 45.263 45.263 0 0 0 3.626-3.115l.922-.824c.293-.26.59-.519.885-.774 2.334-2.025 4.98-4.32 4.98-7.94a6.985 6.985 0 0 0-6.708-7.218Z" />
                </svg>
              </PlainButton>
              <PlainButton>
                <svg color="#262626" fill="#262626" height="24" role="img" viewBox="0 0 24 24" width="24">
                  <path d="M20.656 17.008a9.993 9.993 0 1 0-3.59 3.615L22 22Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" />
                </svg>
              </PlainButton>
              <PlainButton>
                <svg color="#262626" fill="#262626" height="24" role="img" viewBox="0 0 24 24" width="24">
                  <line fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" x1="22" x2="9.218" y1="3" y2="10.083" />
                  <polygon fill="none" points="11.698 20.334 22 3.001 2 3.001 9.218 10.084 11.698 20.334" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" />
                </svg>
              </PlainButton>
            </div>
            <p className="message">
              <strong>{getAccountText()}</strong>
              {" "}
              {instagramShare.message}
            </p>
            <div className="Interactions-Container">
              {instagramShare.reactions > 0 && (
                <p className="like-text">
                  <Trans>Liked by</Trans>
                  {" "}
                  {instagramShare.reactions}
                  {" "}
                  {instagramShare.reactions > 1 ? "people" : "person"}
                </p>
              )}
              {instagramShare.comments > 0 && (
                <p className="comment-text">
                  <Trans>View</Trans>
                  {" "}
                  {instagramShare.comments}
                  {" "}
                  <Trans>comment{instagramShare.comments !== 1 ? "s" : ""}</Trans>
                </p>
              )}
            </div>
          </div>
        </div>
      </InstagramLink>
    </div>
  );
};

export default InstagramPostPreview;
